import React, { useEffect, useState } from 'react';
import { 
  Calendar, 
  Clock, 
  Users, 
  DollarSign, 
  Search, 
  Loader2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  User,
  Phone,
  Mail,
  CreditCard,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Download,
  Send
} from 'lucide-react';
import { BookingsDocument } from '../../types';
import { apiService } from '../../services/api';
import { generateBookingReceipt, sendBookingReceipt } from '../../utils/pdfReceipt';
import { useChatStore } from '../../stores/chatStore';
import toast from 'react-hot-toast';

export const BookingsPage: React.FC = () => {
  const [bookings, setBookings] = useState<BookingsDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedBookings, setExpandedBookings] = useState<Set<string>>(new Set());
  const [sendToUserId, setSendToUserId] = useState<{ [bookingId: string]: string }>({});
  const { sendMessage } = useChatStore();

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    setIsLoading(true);
    try {
      const params: any = {};
      
      if (searchQuery.trim()) {
        params.query = searchQuery.trim();
      }
      if (statusFilter) {
        params.status = statusFilter;
      }

      const bookingsData = await apiService.getAllBookings(params);
      setBookings(bookingsData);
    } catch (error: any) {
      console.error('Failed to load bookings:', error);
      toast.error(error.message || 'Failed to load bookings');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatPrice = (price: number) => {
    return `₹${price}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'partiallypaid':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'init':
        return <RefreshCw className="h-4 w-4 text-blue-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'text-green-600 bg-green-100';
      case 'partiallypaid':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'init':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getGenderText = (gender: number) => {
    switch (gender) {
      case 1: return 'Male';
      case 2: return 'Female';
      default: return 'Other';
    }
  };

  const toggleBookingExpansion = (bookingId: string) => {
    const newExpanded = new Set(expandedBookings);
    if (newExpanded.has(bookingId)) {
      newExpanded.delete(bookingId);
    } else {
      newExpanded.add(bookingId);
    }
    setExpandedBookings(newExpanded);
  };

  const handleSearch = () => {
    loadBookings();
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    loadBookings();
  };

  const canDownloadReceipt = (booking: BookingsDocument) => {
    const status = booking.status?.toUpperCase();
    return status === 'PAID' || status === 'PARTIALLYPAID' || status === 'PARTIALLY_PAID';
  };

  const handleDownloadReceipt = async (booking: BookingsDocument) => {
    try {
      await generateBookingReceipt(booking);
      toast.success('Receipt downloaded successfully!');
    } catch (error) {
      console.error('Failed to generate receipt:', error);
      toast.error('Failed to generate receipt');
    }
  };

  const handleSendReceipt = async (booking: BookingsDocument) => {
    if (!booking.userId) {
      toast.error('User ID not available');
      return;
    }

    try {
      toast.loading('Generating and sending receipt...');
      
      await sendBookingReceipt(
        booking.userId,
        booking,
        apiService.uploadMedia.bind(apiService),
        async (userId: number, messageData: { message: string; type: string }) => {
          await sendMessage(userId, {
            message: messageData.message,
            type: messageData.type
          });
        }
      );

      toast.dismiss();
      toast.success('Receipt sent successfully!');
    } catch (error: any) {
      console.error('Failed to send receipt:', error);
      toast.dismiss();
      toast.error(error.message || 'Failed to send receipt');
    }
  };

  const handleSendReceiptToUser = async (booking: BookingsDocument, targetUserId: string) => {
    const userId = parseInt(targetUserId, 10);
    
    if (isNaN(userId) || userId <= 0) {
      toast.error('Please enter a valid User ID');
      return;
    }

    try {
      toast.loading(`Generating and sending receipt to user ${userId}...`);
      
      await sendBookingReceipt(
        userId,
        booking,
        apiService.uploadMedia.bind(apiService),
        async (userId: number, messageData: { message: string; type: string }) => {
          await sendMessage(userId, {
            message: messageData.message,
            type: messageData.type
          });
        }
      );

      toast.dismiss();
      toast.success(`Receipt sent successfully to user ${userId}!`);
      
      // Clear the input field
      setSendToUserId(prev => {
        const updated = { ...prev };
        delete updated[booking.id];
        return updated;
      });
    } catch (error: any) {
      console.error('Failed to send receipt:', error);
      toast.dismiss();
      toast.error(error.message || 'Failed to send receipt');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-semibold text-gray-900">Bookings</h1>
          <button
            onClick={loadBookings}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Refresh bookings"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, phone, email, or booking ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="init">Init</option>
              <option value="partiallyPaid">Partially Paid</option>
              <option value="Paid">Paid</option>
              <option value="failed">Failed</option>
            </select>
            
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Loading bookings...</p>
            </div>
          </div>
        ) : bookings.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div key={booking.id} className={`rounded-lg shadow-sm border border-gray-200 overflow-hidden ${
                booking.status.toLowerCase() === 'init' ? 'bg-red-50' : 'bg-white'
              }`}>
                <div className="p-4">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{booking.eventName}</h3>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(booking.status)}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <User className="h-4 w-4 mr-2" />
                          <span>{booking.userName}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="h-4 w-4 mr-2" />
                          <span>{booking.phoneNumber}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="h-4 w-4 mr-2" />
                          <span>{booking.tripDate}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <Users className="h-4 w-4 mr-2" />
                          <span>{booking.count} travellers</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <DollarSign className="h-4 w-4 mr-2" />
                          <span>{formatPrice(booking.orderAmount)}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <CreditCard className="h-4 w-4 mr-2" />
                          <span>{formatPrice(booking.amountPaid)} paid</span>
                        </div>
                      </div>

                      {booking.couponCode && (
                        <div className="mb-3">
                          <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            Coupon: {booking.couponCode}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons - Always visible on right side */}
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {canDownloadReceipt(booking) && (
                        <>
                          <button
                            onClick={() => handleDownloadReceipt(booking)}
                            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                            aria-label="Download receipt"
                            title="Download Receipt"
                          >
                            <Download className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleSendReceipt(booking)}
                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-colors"
                            aria-label="Send receipt"
                            title="Send Receipt"
                          >
                            <Send className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => toggleBookingExpansion(booking.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Toggle details"
                      >
                        {expandedBookings.has(booking.id) ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedBookings.has(booking.id) && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Booking Details */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Booking Details</h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-gray-600">Booking ID:</span>
                            <span className="ml-2 font-medium">{booking.id}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Slot ID:</span>
                            <span className="ml-2">{booking.slotId}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Inventory ID:</span>
                            <span className="ml-2">{booking.inventoryId}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Created:</span>
                            <span className="ml-2">{formatDate(booking.createdOn)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Modified:</span>
                            <span className="ml-2">{formatDate(booking.modifiedOn)}</span>
                          </div>
                          {booking.startTime && (
                            <div>
                              <span className="text-gray-600">Start Time:</span>
                              <span className="ml-2">{formatTime(booking.startTime)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Financial Details */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Financial Details</h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-gray-600">Actual Amount:</span>
                            <span className="ml-2 font-medium">{formatPrice(booking.actualAmount)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Ticket Amount:</span>
                            <span className="ml-2">{formatPrice(booking.ticketAmount || 0)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Order Amount:</span>
                            <span className="ml-2 font-medium">{formatPrice(booking.orderAmount)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Amount Paid:</span>
                            <span className="ml-2 font-medium text-green-600">{formatPrice(booking.amountPaid)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Amount Remaining:</span>
                            <span className="ml-2 font-medium text-red-600">{formatPrice(booking.amountRemaining)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Total Discount:</span>
                            <span className="ml-2">{formatPrice(booking.totalDiscount)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Convenience Fee:</span>
                            <span className="ml-2">{formatPrice(booking.convenienceFee)}</span>
                          </div>
                          {booking.gstAmount && (
                            <div>
                              <span className="text-gray-600">GST Amount:</span>
                              <span className="ml-2">{formatPrice(booking.gstAmount)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Payment Details */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Payment Details</h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-gray-600">Payment Count:</span>
                            <span className="ml-2">{booking.paymentCount}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Successful Payments:</span>
                            <span className="ml-2">{booking.successfulPaymentCount}</span>
                          </div>
                          {booking.gateway && (
                            <div>
                              <span className="text-gray-600">Gateway:</span>
                              <span className="ml-2">{booking.gateway}</span>
                            </div>
                          )}
                          {booking.paymentReminderTime && (
                            <div>
                              <span className="text-gray-600">Payment Reminder:</span>
                              <span className="ml-2">{formatDate(booking.paymentReminderTime)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Additional Information</h4>
                        <div className="space-y-2 text-sm">
                          {booking.email && (
                            <div className="flex items-center">
                              <Mail className="h-4 w-4 mr-2 text-gray-400" />
                              <span>{booking.email}</span>
                            </div>
                          )}
                          {booking.source && (
                            <div>
                              <span className="text-gray-600">Source:</span>
                              <span className="ml-2">{booking.source}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-600">Can Edit:</span>
                            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                              booking.canEdit ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {booking.canEdit ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Can Reschedule:</span>
                            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                              booking.canReschedule ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {booking.canReschedule ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">WhatsApp SMS Sent:</span>
                            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                              booking.whatsAppSmsSent ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {booking.whatsAppSmsSent ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">NPS Created:</span>
                            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                              booking.npsIdCreated ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {booking.npsIdCreated ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Travellers */}
                      <div className="md:col-span-2">
                        <h4 className="font-medium text-gray-900 mb-3">Travellers ({booking.travellers.length})</h4>
                        <div className="space-y-2">
                          {booking.travellers.map((traveller, index) => (
                            <div key={traveller.id} className="flex items-center justify-between p-3 bg-white rounded border">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <span className="font-medium">{traveller.name}</span>
                                  <span className="text-sm text-gray-600">({traveller.phoneNumber})</span>
                                  {traveller.primaryUser && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                                      Primary
                                    </span>
                                  )}
                                  {traveller.cancelled && (
                                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                                      Cancelled
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  <span>{getGenderText(traveller.gender)}</span>
                                  {traveller.email && <span className="ml-2">• {traveller.email}</span>}
                                  {traveller.tripLocation && <span className="ml-2">• {traveller.tripLocation.displayText}</span>}
                                </div>
                              </div>
                              <div className="text-right text-sm">
                                {traveller.amountRefunded > 0 && (
                                  <div className="text-red-600">
                                    Refunded: {formatPrice(traveller.amountRefunded)}
                                  </div>
                                )}
                                <div className={`px-2 py-1 rounded-full text-xs ${
                                  traveller.present ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {traveller.present ? 'Present' : 'Absent'}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Links */}
                      <div className="md:col-span-2">
                        <h4 className="font-medium text-gray-900 mb-3">Links & Actions</h4>
                        <div className="flex flex-wrap gap-2">
                          {booking.driveLink && (
                            <a
                              href={booking.driveLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200 transition-colors"
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Drive Link
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                          {booking.remainingAmountLink && (
                            <a
                              href={booking.remainingAmountLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm hover:bg-yellow-200 transition-colors"
                            >
                              <CreditCard className="h-4 w-4 mr-1" />
                              Payment Link
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Send Receipt to User */}
                      {canDownloadReceipt(booking) && (
                        <div className="md:col-span-2">
                          <h4 className="font-medium text-gray-900 mb-3">Send Receipt to User</h4>
                          <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="flex flex-col sm:flex-row gap-2">
                              <div className="flex-1">
                                <label className="block text-xs text-gray-600 mb-1">User ID</label>
                                <input
                                  type="number"
                                  placeholder="Enter User ID"
                                  value={sendToUserId[booking.id] || ''}
                                  onChange={(e) => setSendToUserId(prev => ({
                                    ...prev,
                                    [booking.id]: e.target.value
                                  }))}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      const userId = sendToUserId[booking.id];
                                      if (userId) {
                                        handleSendReceiptToUser(booking, userId);
                                      }
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                />
                              </div>
                              <div className="flex items-end">
                                <button
                                  onClick={() => {
                                    const userId = sendToUserId[booking.id];
                                    if (userId) {
                                      handleSendReceiptToUser(booking, userId);
                                    } else {
                                      toast.error('Please enter a User ID');
                                    }
                                  }}
                                  disabled={!sendToUserId[booking.id] || sendToUserId[booking.id].trim() === ''}
                                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center space-x-2 whitespace-nowrap"
                                >
                                  <Send className="h-4 w-4" />
                                  <span>Send Receipt</span>
                                </button>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              Enter a User ID to send this booking receipt to that user
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Remarks */}
                      {booking.remark && (
                        <div className="md:col-span-2">
                          <h4 className="font-medium text-gray-900 mb-3">Remarks</h4>
                          <p className="text-sm text-gray-700 bg-white p-3 rounded border">{booking.remark}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
